import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { ContentItem, CommitteeMember, Testimonial } from '../types/content';

const DEFAULT_TITLE = 'About The Tournament';
const DEFAULT_DESCRIPTION = 'Celebrating Excellence in African Golf Since 2020';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200';

interface MissionVideo {
  type?: 'youtube' | 'video';
  url: string;
  fallbackImage: string;
  autoplay?: boolean;
  controls?: boolean;
  modestBranding?: boolean;
  showRelated?: boolean;
}

interface PageContent {
  title: string;
  description: string;
  missionStatement: string;
  missionVideo: MissionVideo;
  goals: Array<{ title: string; description: string }>;
  committeeMembers: CommitteeMember[];
  testimonials: Testimonial[];
}

const defaultContent: PageContent = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  missionStatement: '',
  missionVideo: { url: '', fallbackImage: '' },
  goals: [],
  committeeMembers: [],
  testimonials: [],
};

function About() {
  const [pageContent, setPageContent] = useState<PageContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select(`
          id,
          title,
          content_items (*)
        `)
        .eq('page', 'about');

      if (error) throw error;

      if (sections) {
        const headerSection = sections.find(s => s.title === 'Page Header');
        const missionSection = sections.find(s => s.title === 'Mission');
        const committeeSection = sections.find(s => s.title === 'Committee');
        const testimonialsSection = sections.find(s => s.title === 'Testimonials');

        const getContentItem = (section: any, title: string): ContentItem | undefined => {
          return section?.content_items?.find((item: ContentItem) =>
            item.title === title && item.status === 'Published'
          );
        };

        const parseContent = (item: ContentItem | undefined) => {
          if (!item?.content) return null;
          try {
            return typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
          } catch {
            return item.content;
          }
        };

        const titleContent = getContentItem(headerSection, 'Title');
        const descriptionContent = getContentItem(headerSection, 'Description');
        const missionStatement = getContentItem(missionSection, 'Mission Statement');
        const videoContent = getContentItem(missionSection, 'Video Content');
        const goalsContent = getContentItem(missionSection, 'Goals');

        const committeeContent = getContentItem(committeeSection, 'Committee Members');
        const parsedCommitteeContent = parseContent(committeeContent);
        const committeeMembers = Array.isArray(parsedCommitteeContent) ? parsedCommitteeContent : [];

        const testimonialsContent = getContentItem(testimonialsSection, 'Testimonials List');
        const parsedTestimonialsContent = parseContent(testimonialsContent);
        const testimonials = Array.isArray(parsedTestimonialsContent) ? parsedTestimonialsContent : [];

        const parsedGoals = parseContent(goalsContent);

        setPageContent(prev => ({
          title: parseContent(titleContent) || prev.title,
          description: parseContent(descriptionContent) || prev.description,
          missionStatement: parseContent(missionStatement) || '',
          missionVideo: parseContent(videoContent) || prev.missionVideo,
          goals: Array.isArray(parsedGoals) ? parsedGoals : [],
          committeeMembers,
          testimonials,
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasCommitteeMembers = Array.isArray(pageContent.committeeMembers) && pageContent.committeeMembers.length > 0;
  const hasTestimonials = Array.isArray(pageContent.testimonials) && pageContent.testimonials.length > 0;
  const hasGoals = Array.isArray(pageContent.goals) && pageContent.goals.length > 0;
  const hasMissionContent = pageContent.missionStatement || hasGoals || pageContent.missionVideo.url;

  if (loading) {
    return (
      <div className="pt-20 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <section className="relative bg-golf-gray text-white py-16 sm:py-20 md:py-28 lg:py-32">
        <div className="absolute inset-0">
          <img
            src={DEFAULT_IMAGE}
            alt="Golf course aerial view"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-4 sm:mb-6">
              {pageContent.title}
            </h1>
            <p className="text-lg sm:text-xl text-golf-sand">
              {pageContent.description}
            </p>
          </motion.div>
        </div>
      </section>

      {hasMissionContent && (
        <section className="py-12 sm:py-16 md:py-20 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative rounded-3xl overflow-hidden">
                  {pageContent.missionVideo.type === 'youtube' && pageContent.missionVideo.url ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${pageContent.missionVideo.url}?autoplay=${pageContent.missionVideo.autoplay ? 1 : 0}&controls=${pageContent.missionVideo.controls === false ? 0 : 1}&modestbranding=${pageContent.missionVideo.modestBranding ? 1 : 0}&rel=${pageContent.missionVideo.showRelated ? 1 : 0}`}
                      title="Mission Video"
                      className="w-full aspect-[4/3] border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <img
                      src={pageContent.missionVideo.fallbackImage || DEFAULT_IMAGE}
                      alt="Golf Course"
                      className="w-full aspect-[4/3] object-cover"
                    />
                  )}
                </div>
              </motion.div>

              <div className="space-y-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-golf-gray mb-4 sm:mb-6">
                    Our Mission
                  </h2>
                  {pageContent.missionStatement && (
                    <p className="text-lg sm:text-xl text-gray-600 mb-8">
                      {pageContent.missionStatement}
                    </p>
                  )}
                  {hasGoals && (
                    <div className="space-y-4">
                      {pageContent.goals.map((goal, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <div className="w-2 h-2 bg-golf-orange rounded-full flex-shrink-0"></div>
                          <p className="text-gray-600">{goal.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {hasCommitteeMembers && (
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-7xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center text-golf-gray mb-8 sm:mb-12 md:mb-16">
                Planning Committee
              </h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
                {pageContent.committeeMembers.map((member, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="relative mb-4 sm:mb-6">
                      <div className="w-36 h-36 sm:w-44 sm:h-44 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-golf-gray mb-2">
                      {member.name}
                    </h3>
                    <div className="text-golf-orange font-medium mb-3 sm:mb-4">
                      {member.title}
                    </div>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {member.bio}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {hasTestimonials && (
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-golf-gray text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-7xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center mb-8 sm:mb-12 md:mb-16">
                What People Say
              </h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {pageContent.testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8"
                  >
                    <div className="flex items-center gap-4 mb-4 sm:mb-6">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="font-bold text-base sm:text-lg">{testimonial.author}</div>
                        <div className="text-golf-orange text-sm sm:text-base">{testimonial.title}</div>
                      </div>
                    </div>
                    <blockquote className="text-golf-sand text-sm sm:text-base">
                      "{testimonial.quote}"
                    </blockquote>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}

export default About;